import React, { useMemo } from "react";
import { Badge } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { SCHEDULE_TYPE } from "@/utils/api/ExpenseScheduleService";

// ============================================
// Types
// ============================================

export interface ExpenseScheduleTypeBadgeProps {
  /** The schedule type value from SCHEDULE_TYPE */
  scheduleType: string | undefined;

  /** Badge size */
  size?: "xs" | "sm" | "md" | "lg" | "xl";

  /** Badge color */
  color?: string;

  /** Badge variant */
  variant?: "filled" | "light" | "outline" | "dot" | "transparent" | "default" | "white";
}

// ============================================
// Component
// ============================================

const ExpenseScheduleTypeBadgeComponent: React.FC<ExpenseScheduleTypeBadgeProps> = ({
  scheduleType,
  size = "sm",
  color = "blue",
  variant = "outline",
}) => {
  const { t } = useTranslation(["expenseSchedules"]);

  const label = useMemo(() => {
    switch (scheduleType) {
      case SCHEDULE_TYPE.WEEKLY:
        return t("expenseSchedules:scheduleType.weekly");
      case SCHEDULE_TYPE.MONTHLY:
        return t("expenseSchedules:scheduleType.monthly");
      case SCHEDULE_TYPE.YEARLY:
        return t("expenseSchedules:scheduleType.yearly");
      case SCHEDULE_TYPE.ONE_TIME:
        return t("expenseSchedules:scheduleType.oneTime");
      default:
        return scheduleType || "";
    }
  }, [scheduleType, t]);

  if (!label) {
    return null;
  }

  return (
    <Badge color={color} variant={variant} size={size}>
      {label}
    </Badge>
  );
};

export const ExpenseScheduleTypeBadge = React.memo(ExpenseScheduleTypeBadgeComponent);